<template>
  <div class="content-panel">
    <div class="panel-title-wrap">
      <h3 class="panel-title">{{ $t("changePwd.title") }}</h3>
      <span class="panel-sub">{{ $t("changePwd.subtitle") }}</span>
    </div>
    <!-- ===== 未开启两步验证警告 ===== -->
    <div class="twofa-warning" v-if="isCheck">
      <span class="warning-icon">⚠️</span>
      <span class="warning-text">
        {{ $t("changePwd.warning") }}
      </span>
      <button class="warning-btn" @click="goToSecurity">
        {{ $t("changePwd.goEnable") }}
      </button>
    </div>

    <div class="change-pwd-form">
      <div class="form-row">
        <label class="form-label">{{ $t("changePwd.newPwd") }}</label>
        <div class="form-control-wrap">
          <input
            type="password"
            v-model="form.new"
            :placeholder="$t('changePwd.newPwdPlaceholder')"
            class="form-input"
            :disabled="isCheck"
          />
        </div>
      </div>
      <div class="form-row">
        <label class="form-label">{{ $t("changePwd.confirmPwd") }}</label>
        <div class="form-control-wrap">
          <input
            type="password"
            v-model="form.confirm"
            :placeholder="$t('changePwd.confirmPwdPlaceholder')"
            class="form-input"
            :disabled="isCheck"
          />
        </div>
      </div>
      <!-- 邮箱验证码 -->
      <div class="form-row">
        <label class="form-label">{{ $t("changePwd.emailCode") }}</label>
        <div class="form-control-wrap">
          <input
            type="text"
            v-model="form.emailCode"
            :placeholder="$t('changePwd.emailCodePlaceholder')"
            class="form-input"
            maxlength="6"
            :disabled="isCheck"
          />
          <button
            class="btn-captcha"
            @click="openTwoFactorModal"
            :disabled="codeCountdown > 0 || isCheck"
          >
            {{
              codeCountdown > 0
                ? $t("changePwd.resend", { count: codeCountdown })
                : $t("changePwd.sendCode")
            }}
          </button>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn-submit" @click="handleSave" :disabled="isCheck">
          {{ $t("changePwd.confirmBtn") }}
        </button>
      </div>
    </div>

    <!-- ===== 两步验证弹窗 ===== -->
    <TwoFactorModal
      ref="twoFactorModal"
      :desc="$t('changePwd.twofaDesc')"
      @confirm="onTwoFactorConfirm"
    />
  </div>
</template>

<script>
import TwoFactorModal from "@/components/TwoFactorModal.vue";
import { getUserInfo } from "@/utils/fun.js";

export default {
  name: "ChangePwdPanel",
  components: {
    TwoFactorModal,
  },
  data() {
    return {
      info: {},
      form: {
        new: "",
        confirm: "",
        emailCode: "",
      },
      // 验证码倒计时
      codeCountdown: 0,
      codeTimer: null,
    };
  },
  computed: {
    isCheck() {
      //未开启 并且 真实用户
      return !this.isOpen && !this.isMoni;
    },
    isOpen() {
      return this.info.is_two_factor == 2;
    },
    isMoni() {
      return this.info.is_moni == 2;
    },
  },
  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer);
    }
  },
  created() {
    this.info = getUserInfo();
  },
  methods: {
    // ============================================================
    // 0. 去开启两步验证
    // ============================================================
    goToSecurity() {
      this.$emit("go-security");
    },

    // ============================================================
    // 1. 点击「发送验证码」→ 打开两步验证弹窗
    // ============================================================
    openTwoFactorModal() {
      // 校验新密码
      if (this.form.new.length < 6) {
        this.$message.warning(this.$t("changePwd.pwdTooShort"));
        return;
      }
      if (this.form.new !== this.form.confirm) {
        this.$message.warning(this.$t("changePwd.pwdMismatch"));
        return;
      }

      // 检查是否开启两步验证
      if (this.isCheck) {
        this.$message.warning(this.$t("changePwd.enableTwofaFirst"));
        return;
      }

      //模拟用户跳过两步验证
      if (this.info.is_moni == 2) {
        // 调用发送邮箱验证码接口
        this.$api.code
          .sendSafetyCode({
            scene: "update_pwd",
            twoCode: "",
          })
          .then((res) => {
            if (res.code == 1) {
              // 验证失败，显示错误并重置
              this.$message.warning(
                res.msg || this.$t("bindAddress.msgCodeError"),
              );
              return;
            }
            this.$message.success(res.msg);
          });
        return;
      }

      // 打开两步验证弹窗
      this.$refs.twoFactorModal.open();
    },

    // ============================================================
    // 2. 两步验证通过 → 发送邮箱验证码
    // ============================================================
    onTwoFactorConfirm(twoCode) {
      // 设置按钮为验证中状态
      this.$refs.twoFactorModal.setVerifying(true);

      // 调用发送邮箱验证码接口
      this.$api.code
        .sendSafetyCode({
          scene: "update_pwd",
          twoCode,
        })
        .then((res) => {
          if (res.code == 1) {
            // 验证失败，显示错误并重置
            this.$refs.twoFactorModal.setError(
              res.msg || this.$t("changePwd.codeError"),
            );
            return;
          }
          this.$message.success(res.msg);
          this.$refs.twoFactorModal.close();
          this.startCountdown();
        });
    },

    // ============================================================
    // 3. 开始倒计时
    // ============================================================
    startCountdown() {
      this.codeCountdown = 60;
      this.codeTimer = setInterval(() => {
        this.codeCountdown--;
        if (this.codeCountdown <= 0) {
          clearInterval(this.codeTimer);
          this.codeTimer = null;
        }
      }, 1000);
    },

    // ============================================================
    // 4. 确认修改密码
    // ============================================================
    handleSave() {
      if (this.form.new.length < 6) {
        this.$message.warning(this.$t("changePwd.pwdTooShort"));
        return;
      }
      if (this.form.new !== this.form.confirm) {
        this.$message.warning(this.$t("changePwd.pwdMismatch"));
        return;
      }
      if (!this.form.emailCode) {
        this.$message.warning(this.$t("changePwd.enterEmailCode"));
        return;
      }

      // 调用修改密码接口
      this.$api.user
        .updatePwd({
          new_passwd: this.form.new,
          confirm_passwd: this.form.confirm,
          code: this.form.emailCode,
        })
        .then((res) => {
          if (res.code === 1) {
            this.$message.error(res.msg);
            return;
          }
          this.$message.success(this.$t("changePwd.success"));
          this.form.new = "";
          this.form.confirm = "";
          this.form.emailCode = "";
        });
    },
  },
};
</script>

<style scoped>
.content-panel {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.panel-title-wrap {
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.panel-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px 0;
}

.panel-sub {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.35);
}

/* ===== 未开启两步验证警告 ===== */
.twofa-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(255, 77, 79, 0.08);
  border: 1px solid rgba(255, 77, 79, 0.2);
  border-radius: 10px;
  margin-bottom: 20px;
}

.warning-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.warning-text {
  flex: 1;
  font-size: 14px;
  color: rgba(255, 107, 107, 0.9);
  line-height: 1.5;
}

.warning-btn {
  padding: 6px 20px;
  border-radius: 8px;
  background: rgba(255, 77, 79, 0.15);
  border: 1px solid rgba(255, 77, 79, 0.3);
  color: #ff6b6b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
}

.warning-btn:hover {
  background: rgba(255, 77, 79, 0.25);
  color: #fff;
}

/* ===== 表单行 ===== */
.form-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.form-label {
  width: 100px;
  flex-shrink: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.form-control-wrap {
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
}

.form-input {
  flex: 1;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  font-family: inherit;
  max-width: 400px;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.form-input:focus {
  border-color: rgba(0, 180, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(0, 180, 255, 0.05);
}

/* ===== 发送验证码按钮 ===== */
.btn-captcha {
  padding: 10px 20px;
  border-radius: 8px;
  background: rgba(0, 180, 255, 0.06);
  border: 1px solid rgba(0, 180, 255, 0.08);
  color: #00b4ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-captcha:hover:not(:disabled) {
  background: rgba(0, 180, 255, 0.12);
}

.btn-captcha:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== 底部提交按钮 ===== */
.form-actions {
  padding-top: 24px;
  display: flex;
  justify-content: flex-start;
}

.btn-submit {
  padding: 12px 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  border: none;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-submit:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 100, 255, 0.3);
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px 0;
  }
  .form-label {
    width: 100%;
  }
  .form-control-wrap {
    width: 100%;
    flex-wrap: wrap;
  }
  .form-input {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .panel-title {
    font-size: 17px;
  }
  .form-actions {
    width: 100%;
  }
  .btn-submit {
    width: 100%;
    justify-content: center;
    text-align: center;
  }
}
</style>
